import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import {
  DailyProgramEvent,
  DailyProgramOffer,
  DailyProgramVenue,
} from '../../../../../../../shared/types/dailyProgram';
import { useData } from './DataContext';

interface StoredFavorites {
  events: { [key: string]: boolean };
  venues: { [key: string]: boolean };
  offers: { [key: string]: boolean };
}

interface Favorites {
  events: { [key: string]: DailyProgramEvent };
  venues: { [key: string]: DailyProgramVenue };
  offers: { [key: string]: DailyProgramOffer };
}

type ToggleFavoriteParams =
  | { type: 'events'; value: DailyProgramEvent }
  | { type: 'venues'; value: DailyProgramVenue }
  | { type: 'offers'; value: DailyProgramOffer };

interface FavoritesContextProps {
  favorites: Favorites;
  toggleFavorite: (params: ToggleFavoriteParams) => void;
}

const LOCAL_STORAGE_KEY = '@GO_NAVEGANTE_FAVORITES_V2';

const DEFAULT_FAVORITES = { events: {}, venues: {}, offers: {} };

const FavoritesContext = createContext<FavoritesContextProps | undefined>(undefined);

export function WithFavorites({ children }: { children: ReactNode }) {
  const { data, todayLineup } = useData();

  const [favorites, setFavorites] = useState<Favorites>(DEFAULT_FAVORITES);

  useEffect(() => {
    const storedFavorites = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (storedFavorites) {
      const parsedFavorites: StoredFavorites = JSON.parse(storedFavorites);
      const favorites: Favorites = { events: {}, venues: {}, offers: {} };

      const allEvents = Object.values(data.lineup)
        .map(l => l.events)
        .flat();
      const allVenues = todayLineup.venues;
      const allOffers = todayLineup.offers;

      Object.keys(parsedFavorites.events).forEach(key => {
        const item = allEvents.find(e => e.id === key);
        if (item) favorites.events[key] = item;
      });

      Object.keys(parsedFavorites.venues).forEach(key => {
        const item = allVenues.find(v => v.templateId === key);
        if (item) favorites.venues[key] = item;
      });

      Object.keys(parsedFavorites.offers).forEach(key => {
        const item = allOffers.find(o => o.id === key);
        if (item) favorites.offers[key] = item;
      });

      setFavorites(favorites);
    }
  }, []);

  useEffect(() => {
    const storedFavorites: StoredFavorites = { events: {}, venues: {}, offers: {} };

    Object.keys(favorites.events).forEach(key => {
      storedFavorites.events[key] = true;
    });

    Object.keys(favorites.venues).forEach(key => {
      storedFavorites.venues[key] = true;
    });

    Object.keys(favorites.offers).forEach(key => {
      storedFavorites.offers[key] = true;
    });

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(favorites));
  }, [favorites]);

  function toggleFavorite(params: ToggleFavoriteParams) {
    const { type, value } = params;
    const copy = { ...favorites };

    if (type === 'events') {
      if (copy.events[value.id]) {
        delete copy.events[value.id];
      } else if (value) {
        copy.events[value.id] = value;
      }
    } else if (type === 'venues') {
      if (copy.venues[value.templateId]) {
        delete copy.venues[value.templateId];
      } else if (value) {
        copy.venues[value.templateId] = value;
      }
    } else if (type === 'offers') {
      if (copy.offers[value.id]) {
        delete copy.offers[value.id];
      } else if (value) {
        copy.offers[value.id] = value;
      }
    }

    setFavorites(copy);
  }

  return (
    <FavoritesContext.Provider value={{ favorites, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
